import { Fragment, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { LightbulbOutlined } from '@mui/icons-material'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined'
import ThumbUpOffAltOutlinedIcon from '@mui/icons-material/ThumbUpOffAltOutlined'
import { Button, Checkbox, Divider, FormControlLabel, Grid, Link, Typography, useMediaQuery } from '@mui/material'

import BackButton from '../../components/back-button'
import ScreenContainer from '../../components/screen'
import SensorActivationFooter from '../../components/sensor-activation-footer'
import { useActivationContext } from '../../providers/activation-provider'
import { SHORT_SCREEN_HEIGHT } from '../../utils/constants'
import useFolderTheme from '../../utils/hooks/useFolderTheme'

import useStyles from './index.styles'
import { leak5sCommercialLinePlacementTips, leak5sPlacementTips, notHereTips, worksGreatTips } from './tipsContent'

const PlacementTips = () => {
  const classes = useStyles

  const { activationLocationState, loading: activationLoading } = useActivationContext()
  const { nodeTypeName, folderId } = activationLocationState
  const { folderTheme } = useFolderTheme(folderId)

  const navigate = useNavigate()

  const isShortScreen = useMediaQuery(`(max-height:${SHORT_SCREEN_HEIGHT})`)

  const isCommercialLocationType = folderTheme?.locationType === 'Commercial Lines'

  const [isDeviceInSpot, setIsDeviceInSpot] = useState<boolean>(false)

  const handleContinue = async () => {
    if (nodeTypeName === 'leak5s') {
      navigate('/where-to-avoid', { state: activationLocationState })
    } else {
      navigate('/turn-on-sensor', { state: activationLocationState })
    }
  }

  if (!nodeTypeName) return <Navigate to="-1" />

  return (
    <ScreenContainer
      useDarkBackground={true}
      loading={activationLoading}
      headerProps={{
        title: nodeTypeName === 'nbiot_leak' ? 'Activate' : 'Protect high-risk spots',
        leftIcon: <BackButton />,
        useDarkBackground: true,
        progress: nodeTypeName === 'nbiot_leak' ? 10 : 18,
      }}
      footerSpacing={148}
    >
      <Grid container sx={[classes.container, classes.outerContainer(isShortScreen, nodeTypeName === 'nbiot_leak')]}>
        {nodeTypeName === 'nbiot_leak' && (
          <Grid size={12}>
            <Typography
              variant={isShortScreen ? 'h6' : 'h5'}
              sx={[classes.leakFreezeTitle, isShortScreen ? classes.headerContentShort : classes.headerContent]}
            >
              Pick a spot for your sensor
            </Typography>
          </Grid>
        )}

        {nodeTypeName === 'nbiot_leak' && (
          <Grid container={true} size={12} sx={classes.leakFreezeHintRow(isShortScreen)}>
            <Grid size={1} container={true} sx={[classes.leakFreezeHintIconWrapper, classes.lightbulbIconWrapper]}>
              <LightbulbOutlined sx={classes.lightbulbIcon} />
            </Grid>
            <Grid size={10}>
              <Typography variant="body2">After deciding, please take your device to the location where you’ll place it.</Typography>
            </Grid>
          </Grid>
        )}

        {nodeTypeName === 'leak5s' ? (
          <Grid size={12} container={true} spacing={1} sx={classes.leak5sTipsWrapper}>
            {isCommercialLocationType && (
              <Grid size={12}>
                <Typography variant="body2" sx={classes.leak5sCommercialTipText}>
                  Sensors can be placed near appliances, equipment, plumbing, or in a climate-controlled room.
                  <br />
                  Consider areas where you've previously had water leaks, frozen pipes, or temperature-related issues. Examples include:
                </Typography>
              </Grid>
            )}

            {(isCommercialLocationType ? leak5sCommercialLinePlacementTips : leak5sPlacementTips).map((tip, index) => (
              <Fragment key={index}>
                <Grid size={1.5}>
                  <CheckOutlinedIcon sx={classes.leak5sCheckIcon} />
                </Grid>

                <Grid size={10.5}>
                  <Typography variant="body2" sx={classes.leak5sTipText}>
                    <strong>{tip.bold}</strong>
                    {tip.text}
                  </Typography>
                </Grid>
              </Fragment>
            ))}
          </Grid>
        ) : (
          <>
            <Grid size={12} sx={classes.worksWellWrapper} container={true} spacing={1}>
              <Grid size={1.5}>
                <ThumbUpOffAltOutlinedIcon sx={classes.tipsIcon} />
              </Grid>

              <Grid size={10.5}>
                <Typography variant="h6" color="textSecondary" sx={classes.tipsSubheaders}>
                  Works great here
                </Typography>
              </Grid>

              <Grid size={12}>
                <Divider sx={classes.divider} />
              </Grid>

              {worksGreatTips(nodeTypeName).map((tip, index) => (
                <Fragment key={index}>
                  <Grid size={1.5}>
                    <CheckOutlinedIcon color="success" />
                  </Grid>

                  <Grid size={10.5}>
                    <Typography variant="body2" color="textSecondary" sx={classes.tipBodyText}>
                      {tip}
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>

            <Grid size={12} sx={classes.notHereWrapper} container={true} spacing={1}>
              <Grid size={1.5}>
                <ThumbDownOffAltOutlinedIcon sx={classes.tipsIcon} />
              </Grid>

              <Grid size={10.5}>
                <Typography variant="h6" color="textSecondary" sx={classes.tipsSubheaders}>
                  Not here
                </Typography>
              </Grid>

              <Grid size={12}>
                <Divider sx={classes.divider} />
              </Grid>

              {notHereTips(nodeTypeName).map((tip, index) => (
                <Fragment key={index}>
                  <Grid size={1.5}>
                    <CloseOutlinedIcon color="error" />
                  </Grid>

                  <Grid size={10.5}>
                    <Typography variant="body2" color="textSecondary" sx={classes.tipBodyText}>
                      {tip}
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </>
        )}

        {nodeTypeName === 'nbiot_leak' && (
          <FormControlLabel
            control={<Checkbox checked={isDeviceInSpot} onChange={(e) => setIsDeviceInSpot(e.target.checked)} sx={classes.checkbox} />}
            label="I’ve taken the device to its spot."
            sx={classes.checkboxFormControlLabel}
          />
        )}

        <SensorActivationFooter
          primaryButton={
            <Button
              variant="contained"
              disableElevation
              color="secondary"
              disabled={nodeTypeName === 'nbiot_leak' && !isDeviceInSpot}
              onClick={handleContinue}
              sx={{ paddingX: nodeTypeName === 'nbiot_leak' ? 4 : undefined }}
            >
              {nodeTypeName === 'leak5s' ? 'Continue' : 'Next'}
            </Button>
          }
          secondaryButton={
            nodeTypeName === 'leak5s' ? (
              <Link href="https://resources.meshify.com/defender-slim/placement-suggestions/" rel="noopener noreferrer" target="_blank" underline="hover">
                See more placement tips
              </Link>
            ) : undefined
          }
          sxFooter={nodeTypeName === 'nbiot_leak' ? classes.footer : undefined}
        />
      </Grid>
    </ScreenContainer>
  )
}

export default PlacementTips
